import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    MenuItem,
    Paper,
    Snackbar,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { ArrowBack, ExpandMore, FlagOutlined, PhotoLibraryOutlined } from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";
import { answerDeskService, FaqEntry, FaqStatus, FaqTopic } from "../../services/answerDeskService";
import { AnswerEditorDialog } from "./components/AnswerEditorDialog";
import { useAnswerDeskData } from "./useAnswerDeskData";
import { apiErrorMessage, isStale, normalize, scopeOf, targetLabel, timeAgo, topicLabel, TOPICS } from "./answerDeskUtils";

const STATUS_LABEL: Record<FaqStatus, string> = {
    REQUESTED: "Question",
    DRAFT: "Suggested",
    PUBLISHED: "Published",
    ARCHIVED: "Archived",
};
const STATUS_COLOR: Record<FaqStatus, "default" | "primary" | "warning" | "success"> = {
    REQUESTED: "warning",
    DRAFT: "primary",
    PUBLISHED: "success",
    ARCHIVED: "default",
};

type StatusFilter = "ACTIVE" | FaqStatus;
type SortKey = "copied" | "review" | "edited";

const QueueRow: React.FC<{
    entry: FaqEntry;
    meta: string;
    children?: React.ReactNode;
    actions: React.ReactNode;
}> = ({ entry, meta, children, actions }) => (
    <Paper variant="outlined" sx={{ p: { xs: 1.75, sm: 2 }, borderRadius: 3 }}>
        <Stack direction="row" flexWrap="wrap" useFlexGap alignItems="center" spacing={2}>
            <Stack spacing={0.75} sx={{ flex: "1 1 420px", minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500, lineHeight: 1.35 }}>
                    {entry.question}
                </Typography>
                {children}
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    {entry.is_general ? (
                        <Chip size="small" label="General" sx={{ bgcolor: "action.hover" }} />
                    ) : (
                        entry.targets.map((t) => (
                            <Chip key={t.faq_target_id} size="small" variant="outlined" label={targetLabel(t)} sx={{ maxWidth: 280 }} />
                        ))
                    )}
                    {entry.images?.length > 0 && (
                        <Chip
                            size="small"
                            icon={<PhotoLibraryOutlined fontSize="small" />}
                            label={`${entry.images.length} ${entry.images.length === 1 ? "image" : "images"}`}
                            variant="outlined"
                        />
                    )}
                    <Typography variant="caption" color="text.secondary">
                        {meta}
                    </Typography>
                </Stack>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {actions}
            </Stack>
        </Stack>
    </Paper>
);

const SectionTitle: React.FC<{ title: string; count: number; urgent?: boolean }> = ({ title, count, urgent }) => (
    <Typography variant="h6" component="h2" sx={{ fontSize: 17, fontWeight: 500, display: "flex", alignItems: "center", gap: 1 }}>
        {title}
        <Chip size="small" label={count} color={urgent && count > 0 ? "error" : "default"} sx={{ fontWeight: 700 }} />
    </Typography>
);

export const AnswerReview: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const canPublish = user?.role === "OWNER" || user?.role === "MANAGER";
    const { entries, deliveryAreas, loading, error, setError, upsert, removeLocal, reload } = useAnswerDeskData(canPublish);

    const [editing, setEditing] = useState<FaqEntry | null>(null);
    const [busyId, setBusyId] = useState<string | null>(null);
    const [toast, setToast] = useState<{ message: string; severity: "success" | "error" } | null>(null);
    const [filterText, setFilterText] = useState("");
    const [statusFilter, setStatusFilter] = useState<StatusFilter>("ACTIVE");
    const [topicFilter, setTopicFilter] = useState<FaqTopic | "ALL">("ALL");
    const [sortKey, setSortKey] = useState<SortKey>("copied");

    const requested = entries.filter((e) => e.status === "REQUESTED");
    const drafts = entries.filter((e) => e.status === "DRAFT");
    const flagged = entries.filter((e) => e.status === "PUBLISHED" && e.flagged_at);
    const needsTargets = entries.filter((e) => e.status === "PUBLISHED" && !e.is_general && e.targets.length === 0);
    const stale = entries.filter((e) => isStale(e) && !e.flagged_at);
    const mine = entries.filter((e) => e.is_mine && (e.status === "REQUESTED" || e.status === "DRAFT"));

    const run = async (entry: FaqEntry, action: () => Promise<FaqEntry | null>, message: string) => {
        setBusyId(entry.faq_entry_id);
        try {
            const result = await action();
            if (result) upsert(result);
            else removeLocal(entry.faq_entry_id);
            setToast({ message, severity: "success" });
        } catch (err) {
            setToast({ message: apiErrorMessage(err, "That didn't work. Try again."), severity: "error" });
        } finally {
            setBusyId(null);
        }
    };

    const dismiss = (entry: FaqEntry) => {
        if (!window.confirm("Dismiss this question? It moves to the archive.")) return;
        run(entry, async () => {
            await answerDeskService.deleteEntry(entry.faq_entry_id);
            return { ...entry, status: "ARCHIVED" as FaqStatus };
        }, "Dismissed");
    };

    const allAnswers = useMemo(() => {
        const q = normalize(filterText);
        const list = entries.filter((e) => {
            if (statusFilter === "ACTIVE" ? e.status === "ARCHIVED" : e.status !== statusFilter) return false;
            if (topicFilter !== "ALL" && e.topic !== topicFilter) return false;
            if (!q) return true;
            const hay = normalize([e.question, ...e.alt_phrasings, e.answer ?? "", ...e.targets.map(targetLabel)].join(" "));
            return q.split(" ").every((t) => hay.includes(t));
        });
        const reviewTime = (e: FaqEntry) => (e.last_reviewed_at ? new Date(e.last_reviewed_at).getTime() : 0);
        return list.sort((a, b) =>
            sortKey === "copied"
                ? b.copy_count - a.copy_count
                : sortKey === "review"
                  ? reviewTime(a) - reviewTime(b)
                  : new Date(b.update_time).getTime() - new Date(a.update_time).getTime(),
        );
    }, [entries, filterText, statusFilter, topicFilter, sortKey]);

    const asker = (e: FaqEntry) => (e.is_mine ? "you" : e.created_by_email ?? "someone");

    const editButton = (entry: FaqEntry, label = "Edit") => (
        <Button variant="outlined" onClick={() => setEditing(entry)} disabled={busyId === entry.faq_entry_id}>
            {label}
        </Button>
    );

    return (
        <Box sx={{ maxWidth: 1100 }}>
            <Button startIcon={<ArrowBack />} onClick={() => navigate("/answers")} sx={{ mb: 1, ml: -1 }}>
                Find answers
            </Button>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 500 }}>
                {canPublish ? "Review queue" : "My questions and suggestions"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {canPublish
                    ? "Answer what moderators asked, approve what they suggested, fix what they flagged."
                    : "A manager reviews these. Once published, they show up in Find answers for everyone."}
            </Typography>

            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : !canPublish ? (
                <Stack spacing={1.25}>
                    {mine.length === 0 && (
                        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
                            <Typography color="text.secondary">
                                Nothing waiting. When a customer asks something the Answer Desk can't answer, use “Ask a manager”.
                            </Typography>
                        </Paper>
                    )}
                    {mine.map((e) => (
                        <QueueRow
                            key={e.faq_entry_id}
                            entry={e}
                            meta={`${STATUS_LABEL[e.status]} · sent ${timeAgo(e.create_time)}`}
                            actions={
                                <>
                                    <Chip label={e.status === "REQUESTED" ? "Waiting for an answer" : "Waiting for approval"} color={STATUS_COLOR[e.status]} variant="outlined" />
                                    {editButton(e)}
                                </>
                            }
                        >
                            {e.answer && (
                                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }} noWrap>
                                    {e.answer}
                                </Typography>
                            )}
                        </QueueRow>
                    ))}
                </Stack>
            ) : (
                <Stack spacing={4}>
                    {entries.length === 0 && (
                        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
                            <Typography sx={{ mb: 1.5 }}>The Answer Desk is empty.</Typography>
                            <Button
                                variant="contained"
                                disableElevation
                                onClick={async () => {
                                    try {
                                        await answerDeskService.addStarterPack();
                                        await reload();
                                        setToast({ message: "Starter answers added as drafts — check and publish them below.", severity: "success" });
                                    } catch (err) {
                                        setToast({ message: apiErrorMessage(err, "Couldn't add the starter answers."), severity: "error" });
                                    }
                                }}
                            >
                                Add starter answers (as drafts)
                            </Button>
                        </Paper>
                    )}

                    <Stack spacing={1.25} component="section">
                        <SectionTitle title="Questions to answer" count={requested.length} urgent />
                        {requested.length === 0 && <Typography color="text.secondary">No open questions.</Typography>}
                        {requested.map((e) => (
                            <QueueRow
                                key={e.faq_entry_id}
                                entry={e}
                                meta={`Asked by ${asker(e)} · ${timeAgo(e.create_time)}`}
                                actions={
                                    <>
                                        <Button color="inherit" onClick={() => dismiss(e)} disabled={busyId === e.faq_entry_id}>
                                            Dismiss
                                        </Button>
                                        <Button variant="contained" disableElevation onClick={() => setEditing(e)}>
                                            Answer
                                        </Button>
                                    </>
                                }
                            >
                                {e.alt_phrasings.length > 0 && (
                                    <Typography variant="body2" color="text.secondary">
                                        Customer wrote: “{e.alt_phrasings.join("”, “")}”
                                    </Typography>
                                )}
                            </QueueRow>
                        ))}
                    </Stack>

                    <Stack spacing={1.25} component="section">
                        <SectionTitle title="Suggested answers" count={drafts.length} urgent />
                        {drafts.length === 0 && <Typography color="text.secondary">Nothing to approve.</Typography>}
                        {drafts.map((e) => (
                            <QueueRow
                                key={e.faq_entry_id}
                                entry={e}
                                meta={`Suggested by ${asker(e)} · ${timeAgo(e.update_time)}`}
                                actions={
                                    <>
                                        {editButton(e, "Review")}
                                        <Button
                                            variant="contained"
                                            disableElevation
                                            disabled={busyId === e.faq_entry_id}
                                            onClick={() => run(e, () => answerDeskService.publish(e.faq_entry_id), "Published")}
                                        >
                                            Publish
                                        </Button>
                                    </>
                                }
                            >
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ whiteSpace: "pre-line", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                                >
                                    {e.answer}
                                </Typography>
                            </QueueRow>
                        ))}
                    </Stack>

                    <Stack spacing={1.25} component="section">
                        <SectionTitle title="Flagged as outdated" count={flagged.length} urgent />
                        {flagged.length === 0 && <Typography color="text.secondary">Nothing flagged.</Typography>}
                        {flagged.map((e) => (
                            <QueueRow
                                key={e.faq_entry_id}
                                entry={e}
                                meta={`${topicLabel(e.topic)} · last reviewed ${timeAgo(e.last_reviewed_at)}`}
                                actions={
                                    <>
                                        <Button
                                            variant="outlined"
                                            disabled={busyId === e.faq_entry_id}
                                            onClick={() => run(e, () => answerDeskService.markReviewed(e.faq_entry_id), "Marked as still correct")}
                                        >
                                            Still correct
                                        </Button>
                                        <Button variant="contained" disableElevation onClick={() => setEditing(e)}>
                                            Edit answer
                                        </Button>
                                    </>
                                }
                            >
                                <Alert icon={<FlagOutlined fontSize="small" />} severity="error" sx={{ py: 0 }}>
                                    “{e.flag_note}” — {e.flagged_by_email ?? "a moderator"}, {timeAgo(e.flagged_at)}
                                </Alert>
                            </QueueRow>
                        ))}
                    </Stack>

                    {needsTargets.length > 0 && (
                        <Stack spacing={1.25} component="section">
                            <SectionTitle title="Needs a product or category" count={needsTargets.length} urgent />
                            <Typography variant="body2" color="text.secondary">
                                The product, category or brand these answers were tied to was deleted, so they show nowhere.
                            </Typography>
                            {needsTargets.map((e) => (
                                <QueueRow key={e.faq_entry_id} entry={e} meta={topicLabel(e.topic)} actions={editButton(e, "Fix")} />
                            ))}
                        </Stack>
                    )}

                    <Accordion disableGutters variant="outlined" sx={{ borderRadius: 3, "&:before": { display: "none" } }}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <SectionTitle title="Not reviewed in 90 days" count={stale.length} />
                        </AccordionSummary>
                        <AccordionDetails>
                            <Stack spacing={1.25}>
                                {stale.length === 0 && <Typography color="text.secondary">Everything has been checked recently.</Typography>}
                                {stale.map((e) => (
                                    <QueueRow
                                        key={e.faq_entry_id}
                                        entry={e}
                                        meta={`Last reviewed ${timeAgo(e.last_reviewed_at)} · copied ${e.copy_count} times`}
                                        actions={
                                            <>
                                                <Button
                                                    variant="outlined"
                                                    disabled={busyId === e.faq_entry_id}
                                                    onClick={() => run(e, () => answerDeskService.markReviewed(e.faq_entry_id), "Marked as still correct")}
                                                >
                                                    Still correct
                                                </Button>
                                                {editButton(e)}
                                            </>
                                        }
                                    />
                                ))}
                            </Stack>
                        </AccordionDetails>
                    </Accordion>

                    <Stack spacing={1.5} component="section">
                        <SectionTitle title="All answers" count={allAnswers.length} />
                        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                            <TextField
                                size="small"
                                label="Filter"
                                value={filterText}
                                onChange={(e) => setFilterText(e.target.value)}
                                sx={{ flex: "1 1 220px" }}
                            />
                            <TextField
                                select
                                size="small"
                                label="Status"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                                sx={{ minWidth: 150 }}
                            >
                                <MenuItem value="ACTIVE">All but archived</MenuItem>
                                <MenuItem value="PUBLISHED">Published</MenuItem>
                                <MenuItem value="DRAFT">Suggested</MenuItem>
                                <MenuItem value="REQUESTED">Questions</MenuItem>
                                <MenuItem value="ARCHIVED">Archived</MenuItem>
                            </TextField>
                            <TextField
                                select
                                size="small"
                                label="Topic"
                                value={topicFilter}
                                onChange={(e) => setTopicFilter(e.target.value as FaqTopic | "ALL")}
                                sx={{ minWidth: 160 }}
                            >
                                <MenuItem value="ALL">All topics</MenuItem>
                                {TOPICS.map((t) => (
                                    <MenuItem key={t.value} value={t.value}>
                                        {t.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                select
                                size="small"
                                label="Sort"
                                value={sortKey}
                                onChange={(e) => setSortKey(e.target.value as SortKey)}
                                sx={{ minWidth: 170 }}
                            >
                                <MenuItem value="copied">Most copied</MenuItem>
                                <MenuItem value="review">Oldest review first</MenuItem>
                                <MenuItem value="edited">Recently edited</MenuItem>
                            </TextField>
                        </Stack>
                        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
                            {allAnswers.length === 0 && (
                                <Typography color="text.secondary" sx={{ p: 2 }}>
                                    No answers match.
                                </Typography>
                            )}
                            {allAnswers.map((e, i) => (
                                <Stack
                                    key={e.faq_entry_id}
                                    direction="row"
                                    alignItems="center"
                                    flexWrap="wrap"
                                    useFlexGap
                                    spacing={1.5}
                                    sx={{ px: 2, py: 1.25, borderTop: i === 0 ? 0 : 1, borderColor: "divider" }}
                                >
                                    <Box sx={{ flex: "1 1 320px", minWidth: 0 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap title={e.question}>
                                            {e.question}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" noWrap component="div">
                                            {scopeOf(e).label} · {topicLabel(e.topic)} · copied {e.copy_count} · reviewed {timeAgo(e.last_reviewed_at)}
                                        </Typography>
                                    </Box>
                                    <Chip size="small" label={STATUS_LABEL[e.status]} color={STATUS_COLOR[e.status]} variant="outlined" />
                                    {e.status === "ARCHIVED" ? (
                                        <Button
                                            size="small"
                                            disabled={busyId === e.faq_entry_id}
                                            onClick={() =>
                                                run(e, () => answerDeskService.updateEntry(e.faq_entry_id, { status: "DRAFT" }), "Restored as a draft")
                                            }
                                        >
                                            Restore
                                        </Button>
                                    ) : null}
                                    <Button size="small" variant="outlined" onClick={() => setEditing(e)}>
                                        Edit
                                    </Button>
                                </Stack>
                            ))}
                        </Paper>
                    </Stack>
                </Stack>
            )}

            <AnswerEditorDialog
                open={!!editing}
                entry={editing}
                canPublish={canPublish}
                deliveryAreas={deliveryAreas}
                onClose={() => setEditing(null)}
                onSaved={(saved, message) => {
                    const current = editing;
                    setEditing(null);
                    if (saved) upsert(saved);
                    else if (current) removeLocal(current.faq_entry_id);
                    setToast({ message, severity: "success" });
                }}
            />
            <Snackbar
                open={!!toast}
                autoHideDuration={4000}
                onClose={() => setToast(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert onClose={() => setToast(null)} severity={toast?.severity ?? "success"} variant="filled" sx={{ width: "100%" }}>
                    {toast?.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AnswerReview;
